import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { AlertCircle } from 'lucide-react';
import { useAppAuth } from '@/lib/useAppAuth';
import { useOnboardingGate } from '@/lib/auth/useOnboardingGate';
import { useTerritory } from '@/lib/territory/useTerritory';
import { useOpenTerritoryGate } from '@/store/territoryGateStore';
import { useOpenKycGate } from '@/store/kycGateStore';
import { useKycIdentityPending } from '@/lib/kyc/useKycIdentityPending';
import { useCapabilityEnabled, KILL_SWITCH } from '@/lib/launchdarkly/killSwitches';
import {
  getDepositGetStoreQueryKey,
  getDepositListQueryKey,
  getWithdrawalGetBalanceQueryKey,
  useDepositGetStore,
  useDepositInitiateHook,
  useDepositUpdateHook,
} from '@/lib/endpoints';
import type { DepositStatusResponseDto } from '@/lib/models/depositStatusResponseDto';
import type { InitiateDepositResponseDto } from '@/lib/models/initiateDepositResponseDto';
import type { InitiateDepositDto } from '@/lib/models/initiateDepositDto';
import { formatCentsGrouped, formatCurrencyWhole, parseDollarsToCents, sanitizeAmountInput } from '@/lib/format-currency';
import { useAmountInput } from '@/hooks/useAmountInput';
import { parsePaymentApiError } from '@/lib/payments/parseApiError';
import { isDepositLimitExceededError } from '@/lib/depositLimit/types';
import { detectWalletsNatively } from '@/lib/paycom/service';
import { cn } from '@/lib/utils';
import { BottomSheet, Button, SpriteSheetPlayer } from '@/components/ui';
import { formatCrownDollars, type SelectedOffer } from '@/lib/monetization';
import { DepositFormSkeleton } from './DepositFormSkeleton';
import { DepositFail } from './DepositFail';
import { DepositSuccess } from './DepositSuccess';
import { ExpressCheckout } from './ExpressCheckout';
import { MoreOptions } from './MoreOptions';
import { PaycomCheckout, type CheckoutView } from './PaycomCheckout';
import { crownBadgeData, crownBadgeSheet, crownFrames } from '@/lib/crown-frames';

/** Debounce before minting an intent for a freshly-settled amount. */
const MINT_DEBOUNCE_MS = 600;

/**
 * Amount (cents) the on-load bootstrap session is opened with, before the user
 * types anything. Raised to the store minimum when that's higher — the api
 * rejects an initiate below it. The bootstrap amount can never be charged:
 * every pay action stays locked (`paused`) until a valid amount is committed,
 * at which point the session is updated in place to that amount.
 */
const BOOTSTRAP_AMOUNT_CENTS = 100;

/**
 * Deposit intent payload extended with the optional monetization offer binding
 * (v1.3). The generated {@link InitiateDepositDto} doesn't carry these yet, so
 * we widen it locally; the api accepts them and mints the bonus on payment
 * success. Drop this once the Orval client is regenerated.
 */
type InitiateDepositWithOffer = InitiateDepositDto & {
  offerExperienceId?: string;
  offerOptionId?: string;
};

interface DepositFormProps {
  /**
   * Render the success/fail result inline where the form was (popup cashier)
   * instead of in bottom sheets; `onDone` dismisses the flow after success.
   */
  inlineResults?: { onDone: () => void };
}

export function DepositForm({ inlineResults }: DepositFormProps = {}) {
  const queryClient = useQueryClient();
  const { isOnboarded } = useAppAuth();
  const ensureOnboarded = useOnboardingGate('deposit_gate');
  const territory = useTerritory();
  const openTerritoryGate = useOpenTerritoryGate();
  const depositsEnabled = useCapabilityEnabled(KILL_SWITCH.DEPOSITS);
  const kycPending = useKycIdentityPending();
  const openKycGate = useOpenKycGate();
  const initiate = useDepositInitiateHook();
  const updateDeposit = useDepositUpdateHook();

  // The offer option chosen in the detail sheet is carried entirely in the URL
  // (`?offer=<experienceId>&option=<optionId>&amount=<depositCents>&bonus=<bonusCents>`)
  // so it resolves synchronously on first render — the bonus shows the same
  // whether the user navigated here or refreshed, with no follow-up fetch to
  // race. The api re-derives + validates the real bonus on mint (and rejects a
  // deposit ≠ the option's fixed amount), so these display values are safe.
  const [searchParams] = useSearchParams();
  const offerOptionId = searchParams.get('option');
  const offerExperienceId = searchParams.get('offer');
  const offerAmountParam = searchParams.get('amount');
  const offerBonusParam = searchParams.get('bonus');
  const pendingOffer = useMemo<SelectedOffer | null>(() => {
    if (!offerOptionId || !offerExperienceId || offerAmountParam == null || offerBonusParam == null) return null;
    const depositCents = Number.parseInt(offerAmountParam, 10);
    const bonusCents = Number.parseInt(offerBonusParam, 10);
    if (!Number.isInteger(depositCents) || depositCents <= 0 || !Number.isInteger(bonusCents) || bonusCents < 0) {
      return null;
    }
    return { offerExperienceId, offerOptionId, depositCents, bonusCents };
  }, [offerOptionId, offerExperienceId, offerAmountParam, offerBonusParam]);
  const seededOfferRef = useRef(false);

  const storeQuery = useDepositGetStore({
    query: {
      queryKey: [...getDepositGetStoreQueryKey(), isOnboarded] as const,
    },
  });

  const [activeIntent, setActiveIntent] = useState<InitiateDepositResponseDto | null>(null);
  const [selectedOffer, setSelectedOffer] = useState<SelectedOffer | null>(null);
  const [completedDeposit, setCompletedDeposit] = useState<DepositStatusResponseDto | null>(null);
  const [failSheetOpen, setFailSheetOpen] = useState(false);
  const [initError, setInitError] = useState('');
  // Mirrored from PaycomCheckout: which checkout sub-view is up. Drives
  // hiding the amount block on the standalone card screens.
  const [checkoutView, setCheckoutView] = useState<CheckoutView>('main');
  const [mintNonce, setMintNonce] = useState(0);
  const mintedAmountRef = useRef<number | null>(null);
  // Tracks which offer option the live intent was minted for, so a change of
  // selection (including clearing it) forces a re-mint alongside amount changes.
  const mintedOfferRef = useRef<string | null>(null);
  // Last store the form was bound to. Used to reset only on a *genuine* store
  // change (sign in/out) — not on the first resolve or StrictMode's re-invoke,
  // either of which would otherwise clobber the freshly-seeded offer/amount.
  const boundStoreIdRef = useRef<string | null>(null);
  // Last result retained for the success sheet: `BottomSheet` keeps its
  // content mounted ~250ms after `open` flips false so the exit slide can
  // play, and the live state is already cleared by then. (The fail sheet
  // shows fixed copy per design, so it needs no retained result.)
  const lastCompletedRef = useRef<DepositStatusResponseDto | null>(null);
  // Sticky wallet availability. Seeded from native detection and OR'd with
  // every Pay.com session's detection (reported up from PaycomCheckout); it
  // only ever grows, so the buttons don't flicker off when the session is
  // re-minted for a new amount.
  const [walletAvail, setWalletAvail] = useState(() => detectWalletsNatively());
  function reportWallets(w: { applepay: boolean; googlepay: boolean }) {
    setWalletAvail((prev) => {
      const applepay = prev.applepay || w.applepay;
      const googlepay = prev.googlepay || w.googlepay;
      if (applepay === prev.applepay && googlepay === prev.googlepay) return prev;
      return { applepay, googlepay };
    });
  }

  const storeCurrency = storeQuery.data?.currency ?? 'usd';
  const {
    amountInput,
    amountCents: selectedAmount,
    clientError,
    serverError,
    setAmountInput,
    markTouched,
    reset: resetAmount,
  } = useAmountInput({
    minCents: storeQuery.data?.minAmount ?? 0,
    maxCents: storeQuery.data?.maxAmount,
    messages: {
      // Ticket copy (ARC-150), shown "$1,000" style — whole dollars, no cents.
      belowMin: (cents) => `The minimum amount per deposit is ${formatCurrencyWhole(cents, storeCurrency)}.`,
      aboveMax: (cents) => `Each deposit is limited to ${formatCurrencyWhole(cents, storeCurrency)}.`,
    },
  });

  const hardBlocked = !isOnboarded || !!territory?.blocked || !depositsEnabled || !!kycPending;

  // ── Session-on-load (Pay.com) ────────────────────────────────────────
  // A deposit intent + Pay.com session is minted immediately on load with the
  // BOOTSTRAP amount, while the input is still blank — so the live payment
  // methods are already mounted (locked) before the user commits an amount.
  // Once an intent is live, this effect leaves it alone: amount/offer edits
  // are NOT pushed here — they're committed at pay time (`commitAmount`
  // below), inside the wallet sheet's `onValidateSession` or right before a
  // card submit. The methods are `paused` while the input is invalid so a
  // payment can never run without a committed amount.
  useEffect(() => {
    // Hard gates (signed out, blocked territory, kill switch, KYC pending):
    // drop the live methods entirely — the preview + gate flow takes over.
    if (hardBlocked) {
      mintedAmountRef.current = null;
      mintedOfferRef.current = null;
      setActiveIntent(null);
      return;
    }
    // A live session is kept mounted through every edit — clearing the field
    // and retyping must not reload the methods.
    if (activeIntent) return;
    const bootstrap = selectedAmount == null && !clientError;
    if (selectedAmount == null || clientError) {
      if (!bootstrap) return;
      // The bootstrap amount derives from the store minimum, and a URL offer
      // must seed first — the seed drives the amount in this same render
      // pass, so bootstrapping alongside it would mint a throwaway intent.
      if (!storeQuery.data || (pendingOffer && !seededOfferRef.current)) return;
    }
    const amount = selectedAmount ?? Math.max(BOOTSTRAP_AMOUNT_CENTS, storeQuery.data?.minAmount ?? BOOTSTRAP_AMOUNT_CENTS);

    // Only bind the offer while the amount still equals its fixed tier — the
    // api rejects a mismatch. A divergent amount is treated as a plain deposit
    // (the clear-offer effect below also drops the now-stale highlight).
    const effectiveOffer = selectedOffer && selectedOffer.depositCents === amount ? selectedOffer : null;

    let cancelled = false;
    const handle = window.setTimeout(
      async () => {
        try {
          const payload: InitiateDepositWithOffer = { amount };
          if (effectiveOffer) {
            payload.offerExperienceId = effectiveOffer.offerExperienceId;
            payload.offerOptionId = effectiveOffer.offerOptionId;
          }
          const intent = await initiate(payload);
          if (cancelled) return;
          mintedAmountRef.current = amount;
          mintedOfferRef.current = effectiveOffer?.offerOptionId ?? null;
          setActiveIntent(intent);
          setInitError('');
        } catch (err) {
          if (cancelled) return;
          mintedAmountRef.current = null;
          mintedOfferRef.current = null;
          // A failed bootstrap is silent: the preview methods stay up and the
          // first typed amount retries the mint — the user did nothing yet, so
          // there is nothing actionable to tell them.
          // CP-007b: a deposit-limit breach is surfaced by the dedicated gate popup
          // (installDepositLimitInterceptor); don't also show the generic inline error.
          setInitError(bootstrap || isDepositLimitExceededError(err) ? '' : parsePaymentApiError(err));
          setActiveIntent(null);
        }
      },
      // The bootstrap fires immediately — there's no typing to debounce yet.
      // The debounce only matters while no session exists (e.g. a failed
      // bootstrap): typing then re-arms a mint per settled amount, not per
      // keystroke.
      bootstrap ? 0 : MINT_DEBOUNCE_MS,
    );

    return () => {
      cancelled = true;
      window.clearTimeout(handle);
    };
  }, [
    hardBlocked,
    activeIntent,
    selectedAmount,
    clientError,
    selectedOffer,
    initiate,
    mintNonce,
    storeQuery.data,
    pendingOffer,
  ]);

  // ── Pay-time amount commit ───────────────────────────────────────────
  // Called by PaycomCheckout right before a charge runs: for wallets, from
  // inside `checkout.headless(...{ onValidateSession })` while the Apple/
  // Google Pay sheet is up; for cards, right before the submit call. PATCHes
  // the intent + its Pay.com session in place to the committed amount/offer,
  // so the session Pay.com charges always matches what's on screen. The
  // amount lives server-side on the session, so no client `resync()` is
  // needed — wallets re-validate the session after this resolves, and card
  // submits post against the server-held session state.
  const commitAmount = useCallback(async (): Promise<boolean> => {
    if (!activeIntent || selectedAmount == null || clientError) return false;
    const effectiveOffer = selectedOffer && selectedOffer.depositCents === selectedAmount ? selectedOffer : null;
    const offerId = effectiveOffer?.offerOptionId ?? null;
    // Already committed (e.g. a retry, or the mint landed on this amount) —
    // skip the round-trip.
    if (mintedAmountRef.current === selectedAmount && mintedOfferRef.current === offerId) return true;
    const payload: InitiateDepositWithOffer = { amount: selectedAmount };
    if (effectiveOffer) {
      payload.offerExperienceId = effectiveOffer.offerExperienceId;
      payload.offerOptionId = effectiveOffer.offerOptionId;
    }
    try {
      const intent = await updateDeposit(activeIntent.intentId, payload);
      mintedAmountRef.current = selectedAmount;
      mintedOfferRef.current = offerId;
      setActiveIntent(intent);
      setInitError('');
      return true;
    } catch (err) {
      // CP-007b: a deposit-limit breach is surfaced by the dedicated gate popup
      // (installDepositLimitInterceptor); don't also show the generic inline error.
      setInitError(isDepositLimitExceededError(err) ? '' : parsePaymentApiError(err));
      // The aborted attempt still needs a working session for the next tap: a
      // terminal intent or expired session can't be PATCHed, so fall back to a
      // fresh mint (SDK remount) directly at the committed amount. The current
      // charge must still abort either way — the in-flight wallet/card call is
      // bound to the old session.
      try {
        const fresh = await initiate(payload);
        mintedAmountRef.current = selectedAmount;
        mintedOfferRef.current = offerId;
        setActiveIntent(fresh);
      } catch {
        mintedAmountRef.current = null;
        mintedOfferRef.current = null;
        setActiveIntent(null);
      }
      return false;
    }
  }, [activeIntent, selectedAmount, clientError, selectedOffer, updateDeposit, initiate]);

  // The amount stays freely editable: the chosen offer is kept *remembered*
  // rather than discarded when the amount diverges from its fixed tier.
  // `effectiveOffer` (render scope + mint effect) only binds/shows the bonus
  // while the amount equals the tier, so a divergent amount mints a plain
  // deposit (the api rejects a mismatched binding) and the bonus reappears if
  // the user returns to the tier amount.

  // Reset everything when the resolved store *changes* (e.g. sign in/out).
  // Deliberately skips the first resolve (and StrictMode's effect re-invoke,
  // which fires with an unchanged id) so it can't wipe an offer/amount the seed
  // effect just applied — the cause of the bonus only showing after a refresh.
  useEffect(() => {
    const storeId = storeQuery.data?.storeId ?? null;
    if (storeId == null) return;
    if (boundStoreIdRef.current == null) {
      boundStoreIdRef.current = storeId;
      return;
    }
    if (boundStoreIdRef.current === storeId) return;
    boundStoreIdRef.current = storeId;
    resetAmount();
    setActiveIntent(null);
    setSelectedOffer(null);
    setCompletedDeposit(null);
    setFailSheetOpen(false);
    setInitError('');
    mintedAmountRef.current = null;
    mintedOfferRef.current = null;
    seededOfferRef.current = false; // allow the URL offer to re-seed for the new store
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeQuery.data?.storeId]);

  // Seed the offer resolved from the URL params: drive the amount to its tier
  // and remember the offer, once the store is ready. One-shot (the reset effect
  // only re-arms it on a genuine store change), so the user can freely edit the
  // amount afterwards without it snapping back; the mint effect then mints an
  // intent carrying the offer while the amount still matches the tier.
  useEffect(() => {
    if (seededOfferRef.current || !pendingOffer || !storeQuery.data) return;
    seededOfferRef.current = true;
    setSelectedOffer(pendingOffer);
    setAmountInput(formatCentsGrouped(pendingOffer.depositCents));
  }, [pendingOffer, storeQuery.data, setAmountInput]);

  if (storeQuery.isLoading) {
    return <DepositFormSkeleton />;
  }
  if (storeQuery.isError || !storeQuery.data) {
    return (
      <div
        role="alert"
        className="flex items-start gap-3 rounded-[14px] border-2 border-danger-alt/30 bg-danger-alt/[0.04] p-4"
      >
        <AlertCircle className="mt-0.5 size-5 shrink-0 text-danger-alt" />
        <div>
          <p className="text-sm font-semibold text-ink">Couldn't load deposit options</p>
          <p className="mt-1 text-xs text-ink-muted">Refresh the page to try again.</p>
        </div>
      </div>
    );
  }

  const error = serverError || clientError || initError;
  const hasAmount = amountInput.length > 0;
  // The bonus binds (and shows) only while the amount still equals the chosen
  // tier — mirror the mint guard so the displayed Crown Dollars match what mints.
  const effectiveOffer = selectedOffer && selectedOffer.depositCents === selectedAmount ? selectedOffer : null;

  function resetForm() {
    resetAmount();
    setActiveIntent(null);
    setSelectedOffer(null);
    setCompletedDeposit(null);
    setFailSheetOpen(false);
    setInitError('');
    mintedAmountRef.current = null;
    mintedOfferRef.current = null;
  }

  function retry() {
    // The failed intent is terminal — clear it and force a fresh mint for the
    // same amount (keeping any selected offer).
    setFailSheetOpen(false);
    setActiveIntent(null);
    mintedAmountRef.current = null;
    mintedOfferRef.current = null;
    setMintNonce((n) => n + 1);
  }

  // Additive quick-add chips: bump the current amount by the tapped suggested
  // amount (cents, from the store config), clamped to the store maximum.
  function addAmount(cents: number) {
    const current = parseDollarsToCents(amountInput) ?? 0;
    let next = current + cents;
    if (storeQuery.data?.maxAmount != null) next = Math.min(next, storeQuery.data.maxAmount);
    setAmountInput(formatCentsGrouped(next));
  }

  function handleAmountBlur() {
    const cents = parseDollarsToCents(amountInput);
    if (cents != null) {
      setAmountInput(formatCentsGrouped(cents));
    }
  }

  /** Tapping a "preview" method when the live session isn't available routes
   *  the user through the same gates the old Continue button enforced. */
  function handleGate() {
    markTouched();
    if (!ensureOnboarded()) return;
    if (kycPending) {
      // Covers territory `UNRESOLVED` too: the region can't be determined
      // until identity is verified, so the KYC iframe is the actionable step,
      // not the territory block screen. No outcome to await: on approval the
      // shared KYC status query flips, `hardBlocked` turns false and the mint
      // effect brings the live methods up.
      void openKycGate(kycPending.flowKey);
      return;
    }
    if (territory?.blocked) {
      openTerritoryGate({ reason: territory.reason });
      return;
    }
    // Invalid / out-of-range amounts surface via markTouched → clientError.
  }

  // Inline results (popup cashier): the result replaces the form instead of
  // sliding up as a bottom sheet under the dialog.
  if (inlineResults && completedDeposit) {
    return (
      <div className="flex flex-col items-center gap-4">
        <DepositSuccess result={completedDeposit} />
        <Button size="lg" className="w-full" onClick={inlineResults.onDone}>
          Done
        </Button>
      </div>
    );
  }
  if (inlineResults && failSheetOpen) {
    return <DepositFail onRetry={retry} />;
  }

  // The card-details and switch-card sub-views are standalone screens per
  // design — the amount block (and bonus line) stay hidden while one is up.
  const amountHidden = !!activeIntent && (checkoutView === 'newcard' || checkoutView === 'switch');

  return (
    <div className="flex flex-col gap-2">
      {/* ── Amount ─────────────────────────────────────────────── */}
      <div className={cn('flex flex-col items-center gap-2 py-2', amountHidden && 'hidden')}>
        <span className="text-[18px] font-semibold text-ink text-left w-full">Set Amount</span>
        <label
          htmlFor="deposit-amount"
          className="flex w-full cursor-text items-center justify-center overflow-hidden select-none"
        >
          {/* Per design the digits stay purple even while out of range — the
              red *error line below the chips is the only error signal. */}
          <span
            className={cn(
              'text-[72px] font-semibold leading-none tracking-tight transition-colors',
              hasAmount ? 'text-purple' : 'text-ink-muted/40',
            )}
          >
            $
          </span>
          <div className="inline-grid min-w-0 max-w-full">
            <span
              className="invisible col-start-1 row-start-1 whitespace-pre text-[72px] font-semibold leading-none tracking-tight"
              aria-hidden="true"
            >
              {amountInput || '0'}
            </span>
            <input
              id="deposit-amount"
              inputMode="decimal"
              size={1}
              placeholder="0"
              value={amountInput}
              onChange={(e) => setAmountInput(sanitizeAmountInput(e.target.value))}
              onBlur={handleAmountBlur}
              autoComplete="off"
              aria-invalid={!!error}
              aria-label="Amount"
              className={cn(
                'col-start-1 row-start-1 min-w-0 border-0 bg-transparent text-left text-[72px] font-semibold leading-none tracking-tight caret-purple outline-0 transition-colors placeholder:text-ink-muted/40 focus-visible:outline-none',
                hasAmount ? 'text-purple' : 'text-ink-muted/40',
              )}
            />
          </div>
        </label>

        {storeQuery.data.suggestedAmounts.length > 0 && (
          <div className="flex w-full gap-2 overflow-x-auto p-2">
            {storeQuery.data.suggestedAmounts.map((cents) => (
              <button
                key={cents}
                type="button"
                onClick={() => addAmount(cents)}
                className="grow shrink-0 basis-0 rounded-pill border-[1.5px] border-stroke-soft bg-surface px-3 py-1.5 text-[15px] font-medium uppercase text-ink whitespace-nowrap transition-all duration-150 hover:-translate-y-0.5 hover:border-purple/40 active:translate-y-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple/40 focus-visible:ring-offset-2"
              >
                + ${cents / 100}
              </button>
            ))}
          </div>
        )}

        {Boolean(error) && (
          <span role="alert" className="text-center text-[16px] text-danger">
            *{error}
          </span>
        )}
      </div>

      {/* ── Crown Dollars bonus (from the offer picked in the detail sheet) ── */}
      {effectiveOffer && !amountHidden && (
        <div className="flex items-center justify-center gap-1">
          <span className="text-[24px] font-semibold text-ink">+</span>
          <SpriteSheetPlayer
            spriteSheetSrc={crownBadgeSheet}
            sheetWidth={crownBadgeData.meta.size.w}
            sheetHeight={crownBadgeData.meta.size.h}
            frames={crownFrames}
            frameDurationMs={30}
            playback="pingpong"
            loopDelayMs={4000}
            width={30}
          />
          <span className="text-[24px] font-semibold text-ink caret-purple">
            {formatCrownDollars(effectiveOffer.bonusCents)} Crown Dollars
          </span>
        </div>
      )}

      {/* ── Methods ────────────────────────────────────────────── */}
      <div className="flex flex-col gap-2">
        {activeIntent ? (
          <PaycomCheckout
            depositId={activeIntent.intentId}
            // The on-screen amount, NOT the intent's: the wallet sheets open
            // with it natively while the intent lags until the pay-time commit.
            amount={clientError ? null : selectedAmount}
            currency={storeCurrency}
            // Paused while there's no committable amount: mid-edit (null /
            // invalid input). The intent's own amount may lag — it's brought
            // up to date at pay time via `onCommitAmount`.
            paused={selectedAmount == null || !!clientError}
            walletAvail={walletAvail}
            onWalletsDetected={reportWallets}
            onViewChange={setCheckoutView}
            onCommitAmount={commitAmount}
            onSuccess={(result) => {
              lastCompletedRef.current = result;
              setCompletedDeposit(result);
              queryClient.invalidateQueries({ queryKey: getWithdrawalGetBalanceQueryKey() });
              // The wallet activity statement lists deposits — refresh it so
              // the row is there the moment the popup closes.
              queryClient.invalidateQueries({ queryKey: getDepositListQueryKey() });
            }}
            onError={() => setFailSheetOpen(true)}
          />
        ) : (
          <PreviewMethods
            wallets={walletAvail}
            // A logged-in user can only pay once a valid amount is entered, so the
            // buttons are disabled at empty/0/out-of-range rather than no-op'ing.
            // Guests stay enabled so a tap still routes through the login gate.
            disabled={isOnboarded && (!depositsEnabled || selectedAmount == null || !!clientError)}
            onGate={handleGate}
          />
        )}
      </div>

      {/* ── Result sheets (mobile — inline mode returns above instead) ── */}
      {!inlineResults && (
        <>
          <BottomSheet open={!!completedDeposit} onClose={resetForm}>
            {lastCompletedRef.current && <DepositSuccess result={lastCompletedRef.current} />}
          </BottomSheet>
          {/* Dismissing the fail sheet also re-mints: the failed intent is
              terminal, so the methods behind it must get a fresh session. */}
          <BottomSheet open={failSheetOpen} onClose={retry}>
            <DepositFail onRetry={retry} />
          </BottomSheet>
        </>
      )}
    </div>
  );
}

interface PreviewMethodsProps {
  wallets: { applepay: boolean; googlepay: boolean };
  disabled: boolean;
  onGate: () => void;
}

/**
 * Methods shown before a live Pay.com session exists (guest, no/invalid
 * amount, or mid-mint). They mirror the live layout but every tap runs the
 * deposit gate (login, territory, amount validation) instead of charging.
 * No card is known yet, so the card method lives under More Options.
 */
function PreviewMethods({ wallets, disabled, onGate }: PreviewMethodsProps) {
  return (
    <div className="flex flex-col gap-6">
      <ExpressCheckout
        applePayAvailable={wallets.applepay}
        googlePayAvailable={wallets.googlepay}
        disabled={disabled}
        onPay={onGate}
      />
      <MoreOptions onCreditCard={onGate} creditCardDisabled={disabled} />
    </div>
  );
}
